import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { RotateCcw, Send } from 'lucide-react';

export const VersionHistoryDialog = ({ open, onOpenChange, proposal, versionHistory, onRestore }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Version History</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div className="p-4 bg-primary/10 rounded-lg border-2 border-primary">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold">Current Version (v{proposal.version || 1})</span>
            <span className="text-xs text-muted-foreground">Active</span>
          </div>
          <p className="text-sm text-muted-foreground">{proposal.title}</p>
        </div>
        {versionHistory.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">No previous versions</p>
        ) : (
          versionHistory.slice().reverse().map((version) => (
            <div key={`v-${version.version}`} className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold">Version {version.version}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {new Date(version.saved_at).toLocaleString()}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => onRestore(version.version)}>
                    <RotateCcw className="mr-1 h-3 w-3" />
                    Restore
                  </Button>
                </div>
              </div>
              <p className="text-sm font-medium">{version.title}</p>
              {version.description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{version.description}</p>
              )}
              {version.amount && (
                <p className="text-sm text-muted-foreground mt-1">Amount: INR {version.amount.toLocaleString('en-IN')}</p>
              )}
              {version.note && (
                <p className="text-xs text-yellow-600 mt-1">{version.note}</p>
              )}
            </div>
          ))
        )}
      </div>
    </DialogContent>
  </Dialog>
);

export const ProposalEditForm = ({ editForm, setEditForm, onSave, onCancel, categories, companies, contacts }) => (
  <div className="p-6 space-y-4 border rounded-lg bg-card">
    <div>
      <label className="text-sm font-medium mb-2 block">Title</label>
      <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm font-medium mb-2 block">Company</label>
        <Select value={editForm.company_id || '__none__'} onValueChange={(value) => {
          const company = companies.find(c => c.company_id === value);
          setEditForm({ ...editForm, company_id: value === '__none__' ? '' : value, company_name: company?.name || '' });
        }}>
          <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">-- No Company --</SelectItem>
            {companies.map(c => (<SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">Contact</label>
        <Select value={editForm.client_name || '__none__'} onValueChange={(value) => setEditForm({ ...editForm, client_name: value === '__none__' ? '' : value })}>
          <SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">-- No Contact --</SelectItem>
            {contacts.map(c => (<SelectItem key={c.client_id} value={c.name}>{c.name} {c.company_name ? `(${c.company_name})` : ''}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
    </div>
    <div>
      <label className="text-sm font-medium mb-2 block">Category</label>
      <Select value={editForm.category} onValueChange={(value) => setEditForm({ ...editForm, category: value })}>
        <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
        <SelectContent>
          {categories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
    <div>
      <label className="text-sm font-medium mb-2 block">Requirement</label>
      <Textarea value={editForm.requirement || ''} onChange={(e) => setEditForm({ ...editForm, requirement: e.target.value })} rows={3} />
    </div>
    <div>
      <label className="text-sm font-medium mb-2 block">Scope/Area to be Built/Worked On</label>
      <Textarea value={editForm.scope_area || ''} onChange={(e) => setEditForm({ ...editForm, scope_area: e.target.value })} rows={3} />
    </div>
    <div>
      <label className="text-sm font-medium mb-2 block">Final Proposal</label>
      <Textarea value={editForm.final_proposal || ''} onChange={(e) => setEditForm({ ...editForm, final_proposal: e.target.value })} rows={4} />
    </div>
    <div>
      <label className="text-sm font-medium mb-2 block">Amount</label>
      <Input type="number" value={editForm.amount || ''} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} />
    </div>
    <div className="flex gap-2">
      <Button onClick={onSave}>Save Changes</Button>
      <Button variant="outline" onClick={onCancel}>Cancel</Button>
    </div>
  </div>
);

export const ApproverDialog = ({ open, onOpenChange, selectedApprover, setSelectedApprover, teamMembers, onSend }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Send for Internal Approval</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Select Approver</label>
          <Select value={selectedApprover} onValueChange={setSelectedApprover}>
            <SelectTrigger><SelectValue placeholder="Choose approver" /></SelectTrigger>
            <SelectContent>
              {teamMembers.filter(m => ['admin', 'manager'].includes(m.role)).map((member) => (
                <SelectItem key={member.user_id} value={member.user_id}>
                  {member.name} ({member.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSend}>Send</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const SendToClientDialog = ({ open, onOpenChange, recipientEmail, setRecipientEmail, onSend }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Send to Client</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Client Email</label>
          <Input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="client@example.com" />
        </div>
        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => onSend(true)}>
            <Send className="mr-2 h-4 w-4" /> Zoho Sign
          </Button>
          <Button className="flex-1" variant="outline" onClick={() => onSend(false)}>
            Manual Approval
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const ManualApprovalDialog = ({ open, onOpenChange, date, setDate, onApprove }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Record Manual Approval</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Approval Date</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onApprove}>Record Approval</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
